import React from "react"
import { observer } from "mobx-react"
import { Flex, Tooltip, Typography } from "antd"
import {
  Bar,
  BarChart,
  Legend,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from "recharts"

import {
  MerchantStatsType,
  StatsPeriod,
} from "@src/widgets/merchantStats/classes/MerchantStats"
import {
  ChartRow,
  getChartNameByType,
} from "@src/widgets/merchantStats/constants"
import css from "./index.module.css"

type Props = {
  data: ChartRow[]
  period: StatsPeriod
}

const formatWeekday = (
  day: string,
  l: Intl.DateTimeFormatOptions["weekday"] = "long",
) => {
  const today = new Date()
  const dayOfMonth = Number(day)
  const date = new Date(today.getFullYear(), today.getMonth(), dayOfMonth)

  return date.toLocaleDateString("en-US", {
    weekday: l,
  })
}

const formatMonth = (
  month: string,
  l: Intl.DateTimeFormatOptions["month"] = "long",
) => {
  const monthIndex = Number(month) - 1
  const date = new Date(2024, monthIndex, 1)

  return date.toLocaleDateString("en-US", {
    month: l,
  })
}

const formatDayMonth = (day: string, firstDayInPeriod: string) => {
  const today = new Date()
  const dayNum = Number(day)
  const firstDayNum = Number(firstDayInPeriod)

  let month = today.getMonth()
  let year = today.getFullYear()

  if (dayNum < firstDayNum) {
    month += 1
    if (month > 11) {
      month = 0
      year += 1
    }
  }

  const date = new Date(year, month, dayNum)

  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
  })
}

const BARS = [
  {
    dataKey: getChartNameByType(MerchantStatsType.PAYIN),
    fill: "rgba(28, 108, 42, 1)",
    id: MerchantStatsType.PAYIN,
  },
  {
    dataKey: getChartNameByType(MerchantStatsType.PAYOUT),
    fill: "#b5cdb4",
    id: MerchantStatsType.PAYOUT,
  },
  {
    dataKey: getChartNameByType(MerchantStatsType.ON_RAMP),
    fill: "#f2b84b",
    id: MerchantStatsType.ON_RAMP,
  },
  {
    dataKey: getChartNameByType(MerchantStatsType.OFF_RAMP),
    fill: "#fde9a0",
    id: MerchantStatsType.OFF_RAMP,
  },
  {
    dataKey: getChartNameByType(MerchantStatsType.REFUND),
    fill: "#0958d9",
    id: MerchantStatsType.REFUND,
  },
  {
    dataKey: getChartNameByType(MerchantStatsType.TOPUP),
    fill: "rgba(255, 77, 79, 1)",
    id: MerchantStatsType.TOPUP,
  },
]

export const StatsChart = observer(({ data, period }: Props) => {
  const tickFormatter = (value: string) => {
    if (period === "week") return formatWeekday(value)

    if (period === "month")
      return formatDayMonth(value, data.at(0)?.day ?? "01")

    if (period === "year") return formatMonth(value)

    return value
  }

  return (
    <Flex vertical>
      <Typography.Title className={css.title} style={{ margin: 0 }}>
        Turnover
      </Typography.Title>
      <ResponsiveContainer height={400} width="100%">
        <BarChart data={data}>
          <Legend
            align="right"
            formatter={(value) => <Typography.Text>{value}</Typography.Text>}
            verticalAlign="top"
          />
          <XAxis dataKey="day" tickFormatter={tickFormatter} />
          <YAxis tickFormatter={(value) => `$${value}`} />
          <Tooltip />
          {BARS.map(({ dataKey, fill, id }) => (
            <Bar dataKey={dataKey} fill={fill} key={id} stackId="a" />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </Flex>
  )
})
